import React, { useState, useEffect } from 'react';
import { Form, message } from 'antd';
import Card, { CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import TabularList from 'components/TabularList/TabularList';
import { EMDASH } from 'constants';
import Switch from 'components/Switch/Switch';
import RadioCards from 'components/RadioCards/RadioCards';
import providerService from 'services/providers.service';
import commonService from 'services/common.service';

const ProgramInfo = ({ application, isEditable = false }) => {
  const [localApplication, setLocalApplication] = useState(application);
  const [ageGroups, setAgeGroups] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    const fetchAgeGroups = async () => {
      try {
        const groups = await commonService.getAgeGroups();
        setAgeGroups(groups);
      } catch (error) {
        message.error('Failed to load age groups');
        console.error('Error loading age groups:', error);
      }
    };
    fetchAgeGroups();
  }, []);

  const { isEditing, editProps } = useEditCard({
    id: 'program-info',
    onEditStart: () =>
      form.setFieldsValue({
        hccInvite: localApplication?.hccInvite,
        ageGroups: localApplication?.ageGroups?.map((group) => group.id),
        hccEligible: localApplication?.hccEligible,
      }),
    onSave: async () => {
      await form.validateFields();
      const values = form.getFieldsValue();
      const updatedApplication = await providerService.updateApplication(application.id, {
        ...localApplication,
        hccInvite: values.hccInvite,
        ageGroups: values.ageGroups.map((id) => ({ id })),
        hccEligible: values.hccEligible,
      });
      setLocalApplication(updatedApplication);
      form.resetFields();
      message.success('Program information updated successfully');
    },
    onDiscard: () => form.resetFields(),
  });

  return (
    <Card
      title="Program information"
      extra={isEditable ? <CardEdit {...editProps} /> : null}
      noBodyPadding
      stickyHeader
    >
      <Form form={form} disabled={editProps.loading}>
        <TabularList
          indented
          data={[
            {
              title: 'Were you invited to apply for this program?',
              value: isEditing ? (
                <Form.Item
                  name="hccInvite"
                  className="mb-0"
                  valuePropName="checked"
                  rules={[{ required: true, message: 'This field is required' }]}
                >
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              ) : localApplication?.hccInvite ? (
                'Yes'
              ) : (
                'No'
              ),
              titleRequired: true,
            },
            {
              title: 'Which age groups do you serve at this location?',
              value: isEditing ? (
                <Form.Item
                  name="ageGroups"
                  className="mb-0"
                  rules={[{ required: true, message: 'Age groups served is required' }]}
                >
                  <RadioCards
                    size="small"
                    multiple
                    options={ageGroups?.map((group) => ({
                      value: group.id,
                      label: group.description,
                    }))}
                  />
                </Form.Item>
              ) : localApplication?.ageGroups?.length > 0 ? (
                localApplication?.ageGroups?.map((group, idx) => (
                  <span key={group.id}>
                    {group.description}
                    {idx < localApplication.ageGroups.length - 1 && <br />}
                  </span>
                ))
              ) : (
                EMDASH
              ),
              titleRequired: true,
            },
            {
              title: 'Services offered Saturdays and Sundays 6:00am-6:00pm?',
              value: isEditing ? (
                <Form.Item
                  name="hccEligible"
                  className="mb-0"
                  valuePropName="checked"
                  rules={[{ required: true, message: 'This field is required' }]}
                >
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              ) : localApplication?.hccEligible ? (
                'Yes'
              ) : (
                'No'
              ),
              titleRequired: true,
            },
          ]}
        />
      </Form>
    </Card>
  );
};

export default ProgramInfo;
